import { styled } from '@linaria/react';
import { useState } from 'react';
import { baseWhite, secondaryBase } from '../../styles/design-tokens';
import { Icon } from '../icon/icon';
import { Text } from '../typography/typography';
import { getHeight, getIconSize, getLabelSize, getPadding, getRadius } from './chip.helpers';
import { HTMLAttributes } from 'react';

type Props = HTMLAttributes<HTMLDivElement> & {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'check' | 'close' | 'default';
  checked: boolean;
  onChecked: (checked: boolean) => void;
};

export const Chip = ({
  checked,
  onChecked,
  size = 'md',
  radius = 'none',
  variant = 'default',
  style,
  children,
  ...rest
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const contentColor = checked || isHovered || isPressed ? baseWhite : secondaryBase;

  return (
    <StyledChip
      {...rest}
      checked={checked}
      onClick={() => onChecked(!checked)}
      size={size}
      radius={radius}
      variant={variant}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      {variant === 'check' && <Icon name="check" size={getIconSize(size)} color={contentColor} />}
      <Text size={getLabelSize(size)} weight="medium" lineHeight="tight" style={{ color: contentColor }}>
        {children}
      </Text>
      {variant === 'close' && <Icon name="close" size={getIconSize(size)} color={contentColor} />}
    </StyledChip>
  );
};

const StyledChip = styled.div<{
  checked: boolean;
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  radius: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  variant: 'check' | 'close' | 'default';
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: ${({ size }) => getHeight(size)};
  border-radius: ${({ radius }) => getRadius(radius)};
  background-color: ${({ checked }) => (checked ? 'var(--secondary-base)' : 'transparent')};
  border: 1px solid var(--secondary-base);
  padding: ${({ size }) => getPadding(size)};
  cursor: pointer;

  &:hover {
    background-color: var(--secondary-light);
    border-color: var(--secondary-light);
    color: var(--base-white);
  }

  &:active {
    background-color: var(--secondary-light);
    border-color: var(--secondary-darkest);
    color: var(--base-white);
  }
`;
